import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { getGoogleMapsApiKey, loadGoogleMapsApi, validateGoogleMapsConfig } from '../utils/googleMaps';

interface MarkerType {
  lat: number;
  lng: number;
  label: string;
  isInRange?: boolean; // Add property to indicate if florist is in range
}

interface MapViewProps {
  markers: MarkerType[];
  searchedLocation?: { lat: number; lng: number } | null;
  allFlorists?: MarkerType[]; // Add property to show all florists, not just those in range
}

// Component to display when Google Maps fails to load
const MapFallback: React.FC<{
  markers: MarkerType[];
  allFlorists?: MarkerType[];
  searchedLocation?: { lat: number; lng: number } | null;
  errorMessage?: string;
}> = ({ markers, allFlorists, searchedLocation, errorMessage }) => {
  return (
    <div className="h-full rounded-lg overflow-hidden shadow-sm">
      <div className="h-full min-h-[300px] lg:min-h-[400px] bg-[#f8ecd4] border border-brand-primary rounded-lg p-4">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between mb-2">
            <p className="text-brand-primary text-sm font-bold text-center">Florist Locations</p>
            {errorMessage && (
              <span className="text-xs text-gray-500 italic">{errorMessage}</span>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {markers.length > 0 ? (
              <ul className="space-y-2">
                {markers.map((marker, index) => (
                  <li key={index} className="text-brand-primary text-xs flex items-center">
                    <span className="inline-block w-5 h-5 bg-[#967F81] text-white rounded-full flex items-center justify-center mr-2 text-[10px]">
                      {index + 1}
                    </span>
                    <span>{marker.label}</span>
                  </li>
                ))}
                {allFlorists && allFlorists.length > markers.length && (
                  <li className="text-gray-500 text-xs mt-4">
                    <span className="font-medium">Also showing:</span> {allFlorists.length - markers.length} florists outside your delivery area
                  </li>
                )}
              </ul>
            ) : searchedLocation ? (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-brand-primary text-xs">
                  No florists found in your delivery area
                </p>
                {allFlorists && allFlorists.length > 0 && (
                  <p className="text-gray-500 text-xs mt-2">
                    {allFlorists.length} florists available in other areas
                  </p>
                )}
              </div>
            ) : (
              <p className="text-brand-primary text-xs">No florists found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MapView: React.FC<MapViewProps> = ({ markers, searchedLocation, allFlorists = [] }) => {
  const [mapError, setMapError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const searchMarkerRef = useRef<google.maps.Marker | null>(null);
  const outOfRangeMarkersRef = useRef<google.maps.Marker[]>([]);
  const currentInfoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  
  // Memoize markers to prevent unnecessary re-renders
  const memoizedMarkers = useMemo(() => markers, [JSON.stringify(markers)]);
  const memoizedAllFlorists = useMemo(() => allFlorists, [JSON.stringify(allFlorists)]);
  const memoizedSearchLocation = useMemo(() => searchedLocation, [
    searchedLocation?.lat,
    searchedLocation?.lng
  ]);
  
  // Memoize the center calculation function
  const calculateCenter = useCallback(() => {
    // First priority: center on search location if provided
    if (memoizedSearchLocation) {
      return memoizedSearchLocation;
    }
    
    // Second priority: center on in-range markers if available
    if (memoizedMarkers.length > 0) {
      const sum = memoizedMarkers.reduce(
        (acc, marker) => ({ lat: acc.lat + marker.lat, lng: acc.lng + marker.lng }),
        { lat: 0, lng: 0 }
      );
      
      return {
        lat: sum.lat / memoizedMarkers.length,
        lng: sum.lng / memoizedMarkers.length
      };
    }
    
    // Third priority: center on all florists if available
    if (memoizedAllFlorists && memoizedAllFlorists.length > 0) {
      const sum = memoizedAllFlorists.reduce(
        (acc, marker) => ({ lat: acc.lat + marker.lat, lng: acc.lng + marker.lng }),
        { lat: 0, lng: 0 }
      );
      
      return {
        lat: sum.lat / memoizedAllFlorists.length,
        lng: sum.lng / memoizedAllFlorists.length
      };
    }
    
    // Default to Sydney
    return { lat: -33.8688, lng: 151.2093 };
  }, [memoizedMarkers, memoizedSearchLocation, memoizedAllFlorists]);
  
  useEffect(() => {
    // Skip re-initialization if the map is already loaded and markers haven't changed
    if (googleMapRef.current && googleMapsLoaded && !isLoading) {
      // Just update markers on the existing map
      updateMapMarkers();
      return;
    }
    
    // Immediately show fallback if config validation fails
    try {
      validateGoogleMapsConfig();
    } catch (error) {
      console.error('Google Maps config validation error:', error);
      setMapError(true);
      setErrorMessage("Map configuration error");
      setIsLoading(false);
      return;
    }
    
    const initMap = async () => {
      if (!mapRef.current) return;
      
      try {
        setIsLoading(true);
        
        // Load Google Maps API with timeout
        if (!googleMapsLoaded) {
          try {
            await Promise.race([
              loadGoogleMapsApi(),
              new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Google Maps load timeout')), 10000)
              )
            ]);
            setGoogleMapsLoaded(true);
          } catch (error) {
            console.error('Error loading Google Maps API:', error);
            setMapError(true);
            setErrorMessage("Unable to load maps");
            setIsLoading(false);
            return;
          }
        }
        
        // Calculate center point
        const center = calculateCenter();
        
        // Create map instance
        const mapOptions: google.maps.MapOptions = {
          center: center,
          zoom: 12,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: true,
          zoomControlOptions: {
            position: google.maps.ControlPosition.RIGHT_TOP
          },
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        };
        
        // Create the map
        const map = new google.maps.Map(mapRef.current, mapOptions);
        googleMapRef.current = map;
        
        // Update markers
        updateMapMarkers();
        
        setMapError(false);
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        setMapError(true);
        
        // Set specific error message based on the error
        if (error instanceof Error) {
          if (error.message.includes('OverQuotaMapError')) {
            setErrorMessage("Map quota exceeded");
          } else if (error.message.includes('MissingKeyMapError')) {
            setErrorMessage("Invalid map configuration");
          } else if (error.message.includes('RefererNotAllowedMapError')) {
            setErrorMessage("Map access restricted");
          } else {
            setErrorMessage("Map loading failed");
          }
        } else {
          setErrorMessage("Map loading failed");
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    initMap();
    
    // Cleanup function
    return () => {
      clearAllMarkers();
    };
  }, [calculateCenter, googleMapsLoaded]);
  
  // Separate effect to update markers when they change
  useEffect(() => {
    if (googleMapRef.current && googleMapsLoaded && !isLoading) {
      updateMapMarkers();
    }
  }, [memoizedMarkers, memoizedSearchLocation, memoizedAllFlorists]);
  
  // Function to clear all markers
  const clearAllMarkers = useCallback(() => {
    // Clear markers
    markersRef.current.forEach(marker => {
      marker.setMap(null);
    });
    markersRef.current = [];
    
    outOfRangeMarkersRef.current.forEach(marker => {
      marker.setMap(null);
    });
    outOfRangeMarkersRef.current = [];
    
    if (searchMarkerRef.current) {
      searchMarkerRef.current.setMap(null);
      searchMarkerRef.current = null;
    }
  }, []);
  
  // Function to update markers on the map
  const updateMapMarkers = useCallback(() => {
    if (!googleMapRef.current) return;
    
    const map = googleMapRef.current;
    
    // Clear existing markers
    clearAllMarkers();
    
    // Add search location marker
    if (memoizedSearchLocation) {
      try {
        const searchMarker = new google.maps.Marker({
          position: { lat: memoizedSearchLocation.lat, lng: memoizedSearchLocation.lng },
          map: map,
          title: "Your search location",
          animation: google.maps.Animation.DROP,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: '#3b82f6', // Blue color for search location
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            scale: 8
          }
        });
        
        // Add info window for search location
        const infoWindow = new google.maps.InfoWindow({
          content: `<div style="font-family: sans-serif; font-size: 12px; color: #3b82f6; padding: 5px;">Your search location</div>`
        });
        
        searchMarker.addListener('click', () => {
          infoWindow.open(map, searchMarker);
        });
        
        searchMarkerRef.current = searchMarker;
      } catch (error) {
        console.error('Error adding search marker:', error);
      }
    }
    
    // First, add out-of-range florists (grey dots) if provided
    if (memoizedAllFlorists && memoizedAllFlorists.length > 0) {
      try {
        // Filter to get only those that aren't in the markers array (which contains in-range florists)
        const outOfRangeFlorists = memoizedAllFlorists.filter(
          florist => !memoizedMarkers.some(m => m.lat === florist.lat && m.lng === florist.lng)
        );
        
        outOfRangeFlorists.forEach((markerData) => {
          if (!markerData.lat || !markerData.lng) return;
          
          const marker = new google.maps.Marker({
            position: { lat: markerData.lat, lng: markerData.lng },
            map: map,
            title: markerData.label,
            animation: google.maps.Animation.DROP,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              fillColor: '#9ca3af', // Grey color for florists outside delivery area
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 2,
              scale: 8
            }
          });
          
          // Add info window
          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div style="font-family: sans-serif; font-size: 12px; padding: 5px;">
                <span style="color: #9ca3af;">${markerData.label}</span>
                <br>
                <span style="color: #9ca3af; font-size: 10px;">Outside delivery area</span>
              </div>
            `
          });
          
          marker.addListener('click', () => {
            // Close any other open infowindows
            if (currentInfoWindowRef.current) {
              currentInfoWindowRef.current.close();
            }
            infoWindow.open(map, marker);
            currentInfoWindowRef.current = infoWindow;
          });
          
          outOfRangeMarkersRef.current.push(marker);
        });
      } catch (error) {
        console.error('Error adding out-of-range markers:', error);
      }
    }
    
    // Then add florists in range (orange dots)
    try {
      memoizedMarkers.forEach((markerData) => {
        if (!markerData.lat || !markerData.lng) return;
        
        const marker = new google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lng },
          map: map,
          title: markerData.label,
          animation: google.maps.Animation.DROP,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: 'brand-primary', // Orange color for florists in delivery area
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
            scale: 8
          }
        });
        
        // Add info window
        const infoWindow = new google.maps.InfoWindow({
          content: `
            <div style="font-family: sans-serif; font-size: 12px; color: brand-primary; padding: 5px;">
              ${markerData.label}
              <br>
              <span style="font-size: 10px;">Delivers to your location</span>
            </div>
          `
        });
        
        marker.addListener('click', () => {
          infoWindow.open(map, marker);
        });
        
        markersRef.current.push(marker);
      });
    } catch (error) {
      console.error('Error adding in-range markers:', error);
    }
    
    // Update map center if needed
    map.setCenter(calculateCenter());
  }, [memoizedMarkers, memoizedSearchLocation, memoizedAllFlorists, clearAllMarkers, calculateCenter]);

  // Only show empty state if we have no markers and no all florists
  if (memoizedMarkers.length === 0 && (!memoizedAllFlorists || memoizedAllFlorists.length === 0) && !memoizedSearchLocation) {
    return (
      <div className="h-full rounded-lg overflow-hidden shadow-sm">
        <div className="h-full min-h-[300px] lg:min-h-[400px] flex items-center justify-center bg-[#f8ecd4] border border-brand-primary rounded-lg">
          <p className="text-brand-primary text-sm">No florists found</p>
        </div>
      </div>
    );
  }

  // Fallback display when map fails to load
  if (mapError) {
    return (
      <MapFallback 
        markers={memoizedMarkers} 
        allFlorists={memoizedAllFlorists} 
        searchedLocation={memoizedSearchLocation}
        errorMessage={errorMessage}
      />
    );
  }

  return (
    <div className="h-full rounded-lg overflow-hidden shadow-sm">
      <div className="relative h-full min-h-[300px] lg:min-h-[400px]">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-[#f8ecd4] bg-opacity-70 z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-primary"></div>
          </div>
        )}
        <div 
          ref={mapRef} 
          className="w-full h-full min-h-[300px] lg:min-h-[400px] border border-brand-primary rounded-lg"
        ></div>
      </div>
    </div>
  );
};

export default React.memo(MapView); 